/**
 * Base class for theme custom elements.
 *
 * Refs: children with `ref="name"` are exposed as `this.refs.name`, children with `ref="items[]"`
 * as the array `this.refs.items`. Refs inside a nested component belong to that component.
 *
 * Declarative events: `on:click="/method"` calls `method(event)` on the closest component,
 * `on:click="/method/value"` calls `method('value', event)`, `on:click="#id/method"` or
 * `on:click="selector/method"` targets another component.
 */

/** @typedef {Record<string, Element | Element[] | undefined>} Refs */

/** Events handled through `on:<type>` attributes */
const DELEGATED_EVENTS = [
  'click',
  'change',
  'input',
  'submit',
  'keydown',
  'focusin',
  'focusout',
  'pointerenter',
  'pointerleave',
  'pointerdown',
  'pointerup',
];

/** Events that do not bubble: only the element carrying the attribute may trigger them */
const NON_BUBBLING_EVENTS = new Set(['pointerenter', 'pointerleave']);

/**
 * @template {Refs} [T=Refs]
 */
export class Component extends HTMLElement {
  /**
   * Refs that must exist when the component connects.
   * @type {string[]}
   */
  requiredRefs = [];

  /** @type {T | null} */
  #refs = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @returns {T} */
  get refs() {
    if (!this.#refs) this.#refs = this.#collectRefs();
    return this.#refs;
  }

  connectedCallback() {
    this.#refs = null;
    this.#observer = new MutationObserver(() => {
      this.#refs = null;
    });
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['ref'] });

    const missing = this.requiredRefs.filter((name) => {
      const ref = /** @type {Refs} */ (this.refs)[name];
      return !ref || (Array.isArray(ref) && ref.length === 0);
    });
    if (missing.length) {
      throw new Error(`<${this.localName}> is missing required refs: ${missing.join(', ')}`);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#refs = null;
  }

  /**
   * Called after the component's markup was replaced (Section Rendering API, theme editor).
   * Subclasses override it to re-initialise, calling `super.updatedCallback()`.
   */
  updatedCallback() {
    this.#refs = null;
  }

  /** @returns {T} */
  #collectRefs() {
    /** @type {Refs} */
    const refs = {};
    for (const element of this.querySelectorAll('[ref]')) {
      if (closestComponent(element.parentElement) !== this) continue;
      const name = element.getAttribute('ref') || '';
      if (name.endsWith('[]')) {
        const key = name.slice(0, -2);
        const list = /** @type {Element[]} */ (refs[key] || []);
        list.push(element);
        refs[key] = list;
      } else {
        refs[name] = element;
      }
    }
    return /** @type {T} */ (refs);
  }
}

/**
 * @param {Element | null} element
 * @returns {Component | null}
 */
function closestComponent(element) {
  let node = element;
  while (node) {
    if (node instanceof Component) return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * @param {Element} element
 * @param {string} selector
 * @returns {Component | null}
 */
function resolveTarget(element, selector) {
  if (!selector) return closestComponent(element);
  const target = selector.startsWith('#')
    ? document.getElementById(selector.slice(1))
    : document.querySelector(selector);
  return target instanceof Component ? target : null;
}

/** @param {Event} event */
function handleDelegatedEvent(event) {
  const attribute = `on:${event.type}`;
  const origin = event.target;
  if (!(origin instanceof Element)) return;

  const element = NON_BUBBLING_EVENTS.has(event.type)
    ? origin.hasAttribute(attribute) ? origin : null
    : origin.closest(`[on\\:${event.type}]`);
  if (!element) return;

  const value = element.getAttribute(attribute) || '';
  const [selector = '', method = '', ...rest] = value.split('/');
  const component = resolveTarget(element, selector);
  if (!component || !method) return;

  const handler = /** @type {unknown} */ (/** @type {Record<string, unknown>} */ (/** @type {unknown} */ (component))[method]);
  if (typeof handler !== 'function') return;

  if (rest.length) handler.call(component, rest.join('/'), event);
  else handler.call(component, event);
}

for (const type of DELEGATED_EVENTS) {
  document.addEventListener(type, handleDelegatedEvent, { capture: NON_BUBBLING_EVENTS.has(type) });
}
